import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Breadcrumb } from '../components/Breadcrumb';
import { BulkActions } from '../components/admin/AdminActions';
import { ClientManagementActions } from '../components/admin/ClientManagementActions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { canPerformAction } from '../utils/permissions';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_CLIENTS = gql`
  query ListClients {
    listClients {
      id
      firstName
      lastName
      email
      phoneNumber
      isEnabled
      loginDisabled
      createdAt
    }
  }
`;

const LIST_CASES = gql`
  query ListCases {
    listCases {
      items {
        id
        clientId
        status
      }
    }
  }
`;

type SortField = 'name' | 'email' | 'cases' | 'createdAt';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'table' | 'grid';

export function ClientsList() {
  const navigate = useNavigate();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Table controls
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const handleBulkDelete = async () => {
    if (!canPerformAction(user?.role, 'clients', 'delete')) {
      alert('You do not have permission to delete clients.');
      return;
    }
    
    try {
      // TODO: Implement bulk delete mutation
      console.log('Bulk deleting clients:', Array.from(selectedClients));
      setSelectedClients(new Set());
      alert(`${selectedClients.size} clients deleted successfully`);
      refetch();
    } catch (error) {
      console.error('Error bulk deleting clients:', error);
      alert('Error deleting clients');
    }
  };

  const handleBulkExport = () => {
    // Get all filtered clients (not just current page)
    let filteredClients = clients.filter((client: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          client.firstName?.toLowerCase().includes(query) ||
          client.lastName?.toLowerCase().includes(query) ||
          client.email?.toLowerCase().includes(query) ||
          client.phoneNumber?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      if (statusFilter !== 'all') {
        const caseStats = clientCaseStats.get(client.id);
        if (statusFilter === 'active' && (!caseStats || caseStats.active === 0)) return false;
        if (statusFilter === 'inactive' && caseStats && caseStats.active > 0) return false;
      }
      
      return true;
    });

    const selectedData = filteredClients.filter((c: any) => selectedClients.has(c.id));
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Total Cases', 'Active Cases', 'Created Date'].join(','),
      ...selectedData.map((c: any) => {
        const stats = clientCaseStats.get(c.id) || { total: 0, active: 0 };
        return [
          `"${c.firstName} ${c.lastName}"`,
          c.email || '',
          c.phoneNumber || '',
          stats.total,
          stats.active,
          format(new Date(c.createdAt), 'yyyy-MM-dd')
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `selected-clients-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const { data: userData } = useQuery<{ getCurrentUser: { id: string; email: string; firstName: string; lastName: string; role: string } }>(GET_CURRENT_USER);
  const { data: clientsData, loading: clientsLoading, refetch } = useQuery<{ listClients: any[] }>(LIST_CLIENTS, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: casesData } = useQuery<{ listCases: { items: any[] } }>(LIST_CASES);

  const user = userData?.getCurrentUser;
  const clients = clientsData?.listClients || [];
  const allCases = casesData?.listCases?.items || [];

  // Count cases per client
  const clientCaseStats = useMemo(() => {
    const stats = new Map();
    clients.forEach((client: any) => {
      const clientCases = allCases.filter((c: any) => c.clientId === client.id);
      stats.set(client.id, {
        total: clientCases.length,
        active: clientCases.filter((c: any) => c.status !== 'CLOSED' && c.status !== 'SETTLED').length,
      });
    });
    return stats;
  }, [clients, allCases]);

  // Filtering, sorting, and pagination
  const { paginatedClients, totalPages, totalFiltered, stats } = useMemo(() => {
    let filtered = clients.filter((client: any) => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          client.firstName?.toLowerCase().includes(query) ||
          client.lastName?.toLowerCase().includes(query) ||
          client.email?.toLowerCase().includes(query) ||
          client.phoneNumber?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        const caseStats = clientCaseStats.get(client.id);
        if (statusFilter === 'active' && (!caseStats || caseStats.active === 0)) return false;
        if (statusFilter === 'inactive' && caseStats && caseStats.active > 0) return false;
      }
      
      return true;
    });

    // Sort
    filtered.sort((a: any, b: any) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
          bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'cases':
          aVal = clientCaseStats.get(a.id)?.total || 0;
          bVal = clientCaseStats.get(b.id)?.total || 0;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Calculate stats
    const activeClients = filtered.filter((c: any) => {
      const caseStats = clientCaseStats.get(c.id);
      return caseStats && caseStats.active > 0;
    }).length;

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const newClients = filtered.filter((c: any) => 
      new Date(c.createdAt) >= thirtyDaysAgo
    ).length;

    // Pagination
    const totalFiltered = filtered.length;
    const totalPages = Math.ceil(totalFiltered / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedClients = filtered.slice(startIndex, endIndex);

    return { 
      paginatedClients, 
      totalPages, 
      totalFiltered,
      stats: {
        total: clients.length,
        active: activeClients,
        new: newClients,
      }
    };
  }, [clients, searchQuery, statusFilter, sortField, sortDirection, currentPage, pageSize, clientCaseStats]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (setter: any) => (value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedClients(new Set(paginatedClients.map((c: any) => c.id)));
    } else {
      setSelectedClients(new Set());
    }
  };

  const handleSelectClient = (clientId: string, checked: boolean) => {
    const newSelected = new Set(selectedClients);
    if (checked) {
      newSelected.add(clientId);
    } else {
      newSelected.delete(clientId);
    }
    setSelectedClients(newSelected);
  };

  const handleExport = () => {
    const allFilteredClients = clients.filter((client: any) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          client.firstName?.toLowerCase().includes(query) ||
          client.lastName?.toLowerCase().includes(query) ||
          client.email?.toLowerCase().includes(query) ||
          client.phoneNumber?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (statusFilter !== 'all') {
        const caseStats = clientCaseStats.get(client.id);
        if (statusFilter === 'active' && (!caseStats || caseStats.active === 0)) return false;
        if (statusFilter === 'inactive' && caseStats && caseStats.active > 0) return false;
      }
      return true;
    });

    const csvContent = [
      ['Name', 'Email', 'Phone', 'Total Cases', 'Active Cases', 'Created'].join(','),
      ...allFilteredClients.map((c: any) => {
        const caseStats = clientCaseStats.get(c.id) || { total: 0, active: 0 };
        return [
          `"${c.firstName} ${c.lastName}"`,
          c.email || '',
          c.phoneNumber || '',
          caseStats.total,
          caseStats.active,
          format(new Date(c.createdAt), 'yyyy-MM-dd'),
        ].join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (clientsLoading && !clientsData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation user={user} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb />

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-16 z-40">
          <div className="max-w-full px-6">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-4">
                <h1 className="text-lg font-semibold text-aalaw-charcoal">Clients</h1>
                <Badge variant="secondary" className="text-xs">
                  {totalFiltered.toLocaleString()} {totalFiltered === 1 ? 'client' : 'clients'}
                  {totalFiltered !== clients.length && ` of ${clients.length.toLocaleString()} total`}
                </Badge>
                {totalFiltered > 0 && (
                  <span className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages}
                  </span>
                )}
              </div>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    viewMode === 'table'
                      ? 'bg-gray-200 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Table View"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-gray-200 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title="Grid View"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
              
              {/* Page Size */}
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 px-2 text-xs border border-gray-300 rounded-lg"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              
              <Button variant="outline" size="sm" onClick={handleExport}>
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export
              </Button>

              {canPerformAction(user?.role, 'clients', 'create') && (
                <Button size="sm" onClick={() => navigate('/clients/new')}>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Client
                </Button>
              )}
            </div>
          </div>
        </div>
        </header>

      {/* Main Content */}
      <main className="max-w-full px-6 py-6">
        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Total Clients</p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.active.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Active Clients</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">{stats.new.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">New (30 days)</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Bar */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-12 gap-3">
              {/* Search */}
              <div className="col-span-6">
                <Input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => handleFilterChange(setSearchQuery)(e.target.value)}
                  className="w-full h-9 text-sm"
                />
              </div>

              {/* Status Filter */}
              <div className="col-span-3">
                <select
                  value={statusFilter}
                  onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value as any)}
                  className="w-full h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                >
                  <option value="all">All Clients</option>
                  <option value="active">Active Clients</option>
                  <option value="inactive">Inactive Clients</option>
                </select>
              </div>

              {/* Actions */}
              <div className="col-span-3 flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </Button>
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-600 hover:text-aalaw-red"
                  title="Clear all filters"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        <BulkActions
          userRole={user?.role}
          resource="clients"
          selectedCount={selectedClients.size}
          onBulkEdit={() => console.log('Bulk edit clients')}
          onBulkDelete={handleBulkDelete}
          onBulkExport={handleBulkExport}
          className="mb-4"
        />

        {/* Table View */}
        {viewMode === 'table' ? (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedClients.size === paginatedClients.length && paginatedClients.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                      />
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Name
                        <SortIcon field="name" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Email
                        <SortIcon field="email" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-700">
                      Phone
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('cases')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Cases
                        <SortIcon field="cases" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3">
                      <button
                        onClick={() => handleSort('createdAt')}
                        className="flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-aalaw-red"
                      >
                        Created
                        <SortIcon field="createdAt" />
                      </button>
                    </th>
                    <th className="w-24 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedClients.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-12">
                        <p className="text-gray-500">No clients found</p>
                        <p className="text-gray-400 text-sm mt-1">
                          {searchQuery || statusFilter !== 'all'
                            ? 'Try adjusting your filters'
                            : 'Clients will appear here once added'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    paginatedClients.map((client: any) => {
                      const caseStats = clientCaseStats.get(client.id) || { total: 0, active: 0 };
                      return (
                        <tr
                          key={client.id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/clients/${client.id}`)}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedClients.has(client.id)}
                              onChange={(e) => handleSelectClient(client.id, e.target.checked)}
                              className="w-4 h-4 text-aalaw-red border-gray-300 rounded focus:ring-aalaw-red"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-aalaw-red flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-semibold text-sm">
                                  {client.firstName?.[0]?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {client.firstName} {client.lastName}
                                </span>
                                {client.isEnabled === false && (
                                  <Badge variant="secondary" className="ml-2 text-xs bg-gray-200 text-gray-600">
                                    Disabled
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{client.email}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{client.phoneNumber || '—'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">
                                {caseStats.total} total
                              </Badge>
                              {caseStats.active > 0 && (
                                <Badge variant="success" className="text-xs">
                                  {caseStats.active} active
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">
                              {format(new Date(client.createdAt), 'MMM d, yyyy')}
                            </span>
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <ClientManagementActions
                              clientId={client.id}
                              clientName={`${client.firstName} ${client.lastName}`}
                              clientEmail={client.email}
                              isEnabled={client.isEnabled !== false}
                              loginDisabled={client.loginDisabled === true}
                              userRole={user?.role}
                              onView={() => navigate(`/clients/${client.id}`)}
                              onEdit={() => navigate(`/clients/${client.id}/edit`)}
                              onRefresh={refetch}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} clients
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        typeof page === 'string' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                        ) : (
                          <button
                            key={`page-${page}`}
                            onClick={() => goToPage(page)}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-aalaw-red text-white font-semibold'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        ) : (
          /* Grid View */
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedClients.length === 0 ? (
                <div className="col-span-full">
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center">
                        <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No clients found</h3>
                        <p className="text-gray-600">
                          {searchQuery || statusFilter !== 'all'
                            ? 'Try adjusting your filters'
                            : 'Clients will appear here once added'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                paginatedClients.map((client: any) => {
                  const caseStats = clientCaseStats.get(client.id) || { total: 0, active: 0 };
                  return (
                    <Card
                      key={client.id}
                      className="hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/clients/${client.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-aalaw-red flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">
                              {client.firstName?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 truncate">
                              {client.firstName} {client.lastName}
                            </h3>
                            <p className="text-sm text-gray-600 truncate">{client.email}</p>
                            {client.phoneNumber && (
                              <p className="text-sm text-gray-600 mt-1">{client.phoneNumber}</p>
                            )}
                            
                            <div className="flex gap-2 mt-3">
                              {caseStats.total > 0 ? (
                                <>
                                  <Badge variant="secondary" className="text-xs">
                                    {caseStats.total} {caseStats.total === 1 ? 'case' : 'cases'}
                                  </Badge>
                                  {caseStats.active > 0 && (
                                    <Badge variant="success" className="text-xs">
                                      {caseStats.active} active
                                    </Badge>
                                  )}
                                </>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  No cases
                                </Badge>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-500 mt-2">
                              Added {format(new Date(client.createdAt), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>

            {/* Grid Pagination */}
            {totalPages > 1 && (
              <Card className="mt-4">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalFiltered)} of {totalFiltered.toLocaleString()} clients
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                          typeof page === 'string' ? (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-500">{page}</span>
                          ) : (
                            <button
                              key={`page-${page}`}
                              onClick={() => goToPage(page)}
                              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                                currentPage === page
                                  ? 'bg-aalaw-red text-white font-semibold'
                                  : 'border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>
                      
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
      </div>
    </div>
  );
}
